import type { DocSearchRequest, DocSearchResponse } from "@repo/shared/types";
import type { QueryClient } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import { post } from "./_client";

export type DocPage = {
  slug: string;
  title: string;
  markdownContent: string;
};

export type BreadcrumbItem = {
  slug: string;
  title: string;
};

type NavPage = {
  pageId: string;
  slug: string;
  parentSlug: string | null;
  title: string;
};

const API_BASE = process.env.BUN_PUBLIC_API_URL ?? "";

/** Fetch a single doc page by fetching the raw .md file and parsing frontmatter client-side */
export async function getDocPage(slug: string): Promise<DocPage> {
  const res = await fetch(`${API_BASE}/static/${slug}.md`);
  if (!res.ok) throw new Error(`Doc page not found: ${slug}`);
  const raw = await res.text();
  const titleMatch = raw.match(/^title:\s*"?([^"\n]+)/m);
  const title = titleMatch?.[1]?.trim() ?? slug;
  const markdownContent = raw.replace(/^---[\s\S]*?---\n*/, "");
  return { slug, title, markdownContent };
}

const navQueryOptions = queryOptions({
  queryKey: ["docs-nav"],
  queryFn: async () => {
    const res = await fetch(`${API_BASE}/static/nav.json`);
    if (!res.ok) return [] as NavPage[];
    return ((await res.json()) as { pages: NavPage[] }).pages;
  },
  staleTime: Infinity,
});

/** Get breadcrumbs for a slug by walking the nav.json parentSlug chain */
export async function getBreadcrumbs(
  slug: string,
  queryClient: QueryClient,
): Promise<BreadcrumbItem[]> {
  if (!slug || slug === "index") return [];

  const pages = await queryClient.fetchQuery(navQueryOptions);
  const pageMap = new Map(pages.map((p) => [p.slug, p]));

  const crumbs: BreadcrumbItem[] = [];
  const current = pageMap.get(slug);

  // Walk up the parent chain (excluding current page and root)
  if (current) {
    let parentSlug = current.parentSlug;
    let depth = 0;
    while (parentSlug && parentSlug !== "index" && depth < 20) {
      const parent = pageMap.get(parentSlug);
      if (!parent) break;
      crumbs.unshift({ slug: parent.slug, title: parent.title });
      parentSlug = parent.parentSlug;
      depth++;
    }
  }

  return crumbs;
}

/** Search — uses API embeddings */
export function searchDocs(query: string) {
  return post<DocSearchResponse>("/internal/docs/search", { query } satisfies DocSearchRequest);
}
